/**
 * @Copyright (c) Microsoft Corporation.  All rights reserved.
 *
 * @file DebugManifestProvider.ts
 */

import { IClientSideComponentManifest } from '@microsoft/sp-module-interfaces';

import SystemJsWrapper from './SystemJsWrapper';
import { ensureDebugAllowed } from './SPModuleLoader';

const debugManifestsQueryParameterName: string = 'debugManifests';
const debugManifestsFileQueryParameterName: string = 'debugManifestsFile';
const sessionStorageKey: string = 'sp-module-loader_debugManifests';

export interface IManifestMap {
  [id: string]: IClientSideComponentManifest;
}

interface IManifestScript {
  getManifests: () => IClientSideComponentManifest[];
}

/**
 * This class assists with parsing debug manifests from the URL fragment or from sessionStorage.
 *
 * --- NEW APPROACH ---
 * We allow developers to specify a set of manifests in the URL to override the manifests we get from the server.
 * The format is: <url-to-page>.aspx#debugManifestsFile=<path to manfiests init script>
 *
 * When a manifest file is specified in the URL, we cache its contents in sessionStorage so the developer doesn't need
 * to supply it on every request. This class also reads the "reset=1" query parameter to clear the cached debug
 * manifests from sessionStorage.
 *
 * --- OLD APPROACH ---
 * We allow developers to specify a set of manifests in the URL to override the manifests we get from the server.
 * The format is: <url-to-page>.aspx#debugManifests=<URL-encoded JSON object>
 * And the format of the JSON object is:
 *  {
 *    "manifest-id-1": { <manifest object> },
 *    "manifest-id-2": { <manifest object> }
 *  }
 *
 * When a manifest is specified in the URL, we cache it in sessionStorage so the developer doesn't need to
 * supply it on every request. This class also reads the "reset=1" query parameter to clear the cached debug
 * manifests from sessionStorage.
 *
 * This is a temporary implementation until we update the build tools to support a more developer-friendly approach.
 * VSO#201640.
 */
export default class DebugManifestProvider {
  private static _instance: DebugManifestProvider;

  public static get instance(): DebugManifestProvider {
    if (!this._instance) {
      this._instance = new DebugManifestProvider();
    }

    return this._instance;
  }

  public getDebugManifests(noCache: boolean = false, alternativeManifestsFiles?: string[]): Promise<IManifestMap> {
    return new Promise<IManifestMap>(
      (resolve: (value: IManifestMap) => void): void => {
        const localDebugManifests: IManifestMap = this._parseManifestsFromFragementQueryParameter();

        if (window.location.search.match(/\breset\b/)) { // Reset the debug manifests
          try {
            window.sessionStorage.removeItem(sessionStorageKey);
          } catch (e) {
            /* tslint:disable:no-console */
            console.debug(`Error clearing session storage. Error: ${e}`);
            /* tslint:enable:no-console */
          }

          resolve({});
        } else {
          let existingManifests: IManifestMap = {};
          try {
            existingManifests = JSON.parse(window.sessionStorage.getItem(sessionStorageKey)) || {};
          } catch (e) {
            /* tslint:disable:no-console */
            console.debug(`Error querying session storage. Error: ${e}`);
            /* tslint:enable:no-console */
          }

          Object.keys(existingManifests).forEach((existingManifestKey: string) => {
            if (!localDebugManifests[existingManifestKey]) {
              localDebugManifests[existingManifestKey] = existingManifests[existingManifestKey];
            }
          });

          const queryParameterManifestsFile: string = this._parseManifestsFileFromFragementQueryParameter(false);
          const manifestsFiles: string[] = !!queryParameterManifestsFile
                                             ? [queryParameterManifestsFile]
                                             : alternativeManifestsFiles;
          if (manifestsFiles && manifestsFiles.length > 0 && ensureDebugAllowed()) {
            const tryGetDebugManifests: (promise: Promise<IManifestScript>) => Promise<void> =
              (promise: Promise<IManifestScript>): Promise<void> =>
                promise.then((manifestsScript: IManifestScript) => {
                  this._parseManifestsFileFromFragementQueryParameter(true); // Clear the parameter only if file loaded

                  const manifests: IClientSideComponentManifest[] = manifestsScript.getManifests();
                  manifests.forEach((manifest: IClientSideComponentManifest) => {
                    localDebugManifests[manifest.id] = manifest;
                  });

                  if (!noCache) {
                    this._trySetSessionStorageManifests(localDebugManifests);
                  }

                  resolve(localDebugManifests);
                });

            let debugManifestsScriptPromise: Promise<void> =
              tryGetDebugManifests(SystemJsWrapper.instance.loadScript(manifestsFiles[0]));

            // We may have multiple URLs for the debug manifests file (i.e. - one for HTTPS, one for HTTP),
            // so we'll try them sequentally until one of them is successfully loaded.
            for (let i: number = 1; i < manifestsFiles.length; i++) {
              const oldPromise: Promise<void> = debugManifestsScriptPromise;
              debugManifestsScriptPromise = oldPromise.catch(() =>
                tryGetDebugManifests(SystemJsWrapper.instance.loadScript(manifestsFiles[i])));
            }

            debugManifestsScriptPromise.catch((error: Error) => {
              alert(`Error loading debug manifest script. (${error.message ? error.message : error}) \n\n` +
                    'Ensure loading unsafe scripts is allowed.');
            });
          } else {
            if (!noCache) {
              this._trySetSessionStorageManifests(localDebugManifests);
            }

            resolve(localDebugManifests);
          }
        }
      });
  }

  private _trySetSessionStorageManifests(localDebugManifests: IManifestMap): void {
    try {
      window.sessionStorage.setItem(sessionStorageKey, JSON.stringify(localDebugManifests));
    } catch (e) {
      /* tslint:disable:no-console */
      console.debug(`Error setting session storage. Error: ${e}`);
      /* tslint:enable:no-console */
    }
  }

  private _parseManifestsFileFromFragementQueryParameter(clearParamter: boolean): string {
    return this._getAndClearFragmentQueryParameter(debugManifestsFileQueryParameterName, clearParamter);
  }

  private _parseManifestsFromFragementQueryParameter(): IManifestMap {
    const debugManifestsString: string = this._getAndClearFragmentQueryParameter(debugManifestsQueryParameterName);
    if (debugManifestsString) {
      try {
        return JSON.parse(debugManifestsString);
      } catch (e) {
        /* tslint:disable:no-console */
        console.debug(`Error parsing manifests query parameter. Error: ${e}`);
        /* tslint:enable:no-console */
      }
    } else {
      return {};
    }
  }

  private _getAndClearFragmentQueryParameter(parameter: string, clearParamter: boolean = true): string {
    let result: string = undefined;

    if (window.location.hash) {
      const fragment: string = window.location.hash.indexOf('#') === 0
        ? window.location.hash.substr(1)
        : window.location.hash;
      const fragmentQueryParameters: { [parameterId: string]: string } = this._parseQueryParameters(fragment);

      if (fragmentQueryParameters &&
          fragmentQueryParameters[parameter]) {
        result = decodeURIComponent(fragmentQueryParameters[parameter]);

        if (clearParamter) {
          // Re-generate the fragment without the parameter.
          const newFragment: string = fragment.replace(`${parameter}=${fragmentQueryParameters[parameter]}`, '');
          if (newFragment !== '') {
            window.history.replaceState('',
                                        document.title,
                                        `${window.location.pathname}${window.location.search}#${newFragment}`);
          } else {
            window.history.replaceState('',
                                        document.title,
                                        `${window.location.pathname}${window.location.search}`);
          }
        }
      }
    }

    return result;
  }

  /**
   * Parse a string as querystring parameters. VSO:201753 to replace with standard library.
   */
  private _parseQueryParameters(queryParameterString: string): { [parameterId: string]: string } {
    if (queryParameterString) {
      if (queryParameterString.indexOf('?') === 0) {
        queryParameterString = queryParameterString.substr(1);
      }

      const queryParameters: string[] = queryParameterString.split('&');
      const result: { [parameterId: string]: string } = {};

      queryParameters.forEach((queryParameter: string) => {
        const equalsIndex: number = queryParameter.indexOf('=');
        if (equalsIndex === -1) {
          result[queryParameter] = '';
        } else {
          result[queryParameter.substr(0, equalsIndex)] = queryParameter.substr(equalsIndex + 1);
        }
      });

      return result;
    } else {
      return undefined;
    }
  }
}



// WEBPACK FOOTER //
// /src/DebugManifestProvider.ts


// WEBPACK FOOTER //
// webpack:///src/DebugManifestProvider.ts